import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Anchor, Button, Fieldset, Group, NativeSelect, Text, Title } from '@mantine/core';
import { FORMAT_RULES } from '@quizee/core';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, errorMessage } from '../api/client';
import type { Brand } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { ErrorNote, ProblemList } from '../components/ErrorNote';
import { InteractionFields } from '../components/InteractionFields';
import { Loading } from '../components/Loading';
import { formatWhat, isFormat } from '../format';
import { buildInteractionContent, emptyDraft, type InteractionDraft } from '../interaction/draft';

/**
 * Building one of anything, before it has been saved.
 *
 * The format arrives in the address, chosen on the screen before this one, and everything that
 * differs between formats is handled by the fields themselves. This page only knows about brands,
 * saving, and what to do when either fails.
 */
export function NewInteraction() {
  const { org } = useAuth();
  const orgId = org?.org_id ?? null;
  const navigate = useNavigate();
  const { format: formatParam } = useParams<{ format: string }>();
  const format = formatParam !== undefined && isFormat(formatParam) ? formatParam : null;

  const [brands, setBrands] = useState<readonly Brand[] | null>(null);
  const [brandId, setBrandId] = useState('');
  const [brandProblem, setBrandProblem] = useState<string | null>(null);
  const [draft, setDraft] = useState<InteractionDraft | null>(() =>
    format === null ? null : emptyDraft(format),
  );
  const [problems, setProblems] = useState<readonly string[]>([]);
  const [saving, setSaving] = useState(false);

  // Walking from one format's blank form to another's replaces the draft. Anything already typed
  // belonged to the old format, so keeping it would only carry rubbish across.
  useEffect(() => {
    setDraft((current) => {
      if (format === null) return null;
      return current !== null && current.format === format ? current : emptyDraft(format);
    });
  }, [format]);

  const loadBrands = useCallback(() => {
    if (orgId === null) return;
    setBrandProblem(null);
    setBrands(null);
    api
      .brands(orgId)
      .then((body) => {
        setBrands(body.brands);
        setBrandId((current) => (current !== '' ? current : (body.brands[0]?.brand_id ?? '')));
      })
      .catch((error: unknown) => setBrandProblem(errorMessage(error)));
  }, [orgId]);

  useEffect(loadBrands, [loadBrands]);

  if (format === null) {
    return (
      <>
        <Title order={1} size="h2">
          We do not make that
        </Title>
        <Text mt="sm">
          That address does not name anything quizee can build.{' '}
          <Anchor component={Link} to="/interactions/new">
            Choose what to make
          </Anchor>
          .
        </Text>
      </>
    );
  }

  const rules = FORMAT_RULES[format];
  const name = rules.label.toLowerCase();

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (orgId === null || draft === null) return;

    const built = buildInteractionContent(draft);
    if (!built.ok) {
      setProblems(built.errors);
      return;
    }
    if (brandId === '') {
      setProblems([`Choose which brand this ${name} belongs to.`]);
      return;
    }

    setProblems([]);
    setSaving(true);
    try {
      const body = await api.createInteraction(orgId, brandId, built.content);
      void navigate(`/interactions/${body.interaction.interaction_id}`, { replace: true });
    } catch (error) {
      setProblems([errorMessage(error)]);
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <Text size="sm" mb="sm">
        <Anchor component={Link} to="/interactions/new" size="sm">
          Back to formats
        </Anchor>
      </Text>
      <Title order={1} size="h2">
        New {name}
      </Title>
      <Text size="sm" c="dimmed" mt="sm" mb="lg">
        {formatWhat(format)} You can change any of this before you publish.
      </Text>

      {brandProblem !== null && <ErrorNote message={brandProblem} onRetry={loadBrands} />}
      {brandProblem === null && brands === null && <Loading label="Loading your brands" />}
      {brands !== null && brands.length === 0 && (
        <ErrorNote
          message={`This publisher has no brands yet, so there is nowhere to put a ${name}. Ask an owner to add one.`}
        />
      )}

      <ProblemList problems={problems} />

      <form onSubmit={(event) => void submit(event)}>
        <section className="editor-content-panel" aria-label="Interaction content">
          <div className="editor-panel-heading">
            <h2>Write your {name}</h2>
            <span>Draft · Not published</span>
          </div>
          {brands !== null && brands.length > 1 && (
            <Fieldset variant="unstyled" disabled={saving} mb="md">
              <NativeSelect
                id="brand"
                label="Brand"
                description={`Which title this ${name} runs on.`}
                value={brandId}
                onChange={(event) => setBrandId(event.target.value)}
                data={brands.map((brand) => ({ value: brand.brand_id, label: brand.name }))}
                style={{ maxWidth: '18rem' }}
              />
            </Fieldset>
          )}

          {draft !== null && (
            <InteractionFields draft={draft} onChange={setDraft} disabled={saving} />
          )}
        </section>

        <Group className="editor-save-bar" mt="lg" justify="space-between">
          <Text size="sm" c="dimmed">
            Save your draft, then review it before publishing.
          </Text>
          <Button type="submit" disabled={saving || brands === null || draft === null}>
            {saving ? 'Saving...' : `Save ${name}`}
          </Button>
        </Group>
      </form>
    </>
  );
}
